import { TextButton, typography } from '@joor/design-system'
import styled from 'styled-components/macro'

import {
  OrderDiscountType,
  OrderPriceModifierType,
} from '__generated__/atlas-types'

import { Number as NumberInput } from 'components/Core'

import { sendPaymentRequestIds } from '../../orders.ids'

export const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  &.disabled {
    & span,
    button {
      color: var(--color-primary-400);
      pointer-events: none;
    }
  }
`

export const StyledInputTitle = styled.span`
  ${typography.alpha.primaryActive.body3}
  margin-bottom: 4px;
`

export const StyledSwitchFormat = styled.div`
  ${typography.alpha.primaryActive.body3}
`

export const StyledNumberInput = styled(NumberInput)`
  position: relative;
  input {
    min-width: 100%;
    text-align: left;
    border-color: var(--color-primary-400);
    color: var(--color-primary-800);
    padding: 4px 8px;
    height: 30px;
    border-radius: 5px;
    font-size: var(--size-font-small);
    box-sizing: border-box;
  }
  span {
    top: 7px !important;
    font-size: var(--size-font-small);
  }
`

export const RequiredAstherisk = styled.span`
  color: red !important; // important so it is red ALWAYS like the other mandatory inputs
`

const FormatSwitch = ({
  selectedFormat,
  onFormatChange,
  percentFormatId,
  valueFormatId = sendPaymentRequestIds.numericInputValue,
}: {
  selectedFormat: OrderDiscountType | OrderPriceModifierType
  onFormatChange: (selectedType: 'VALUE' | 'PERCENT') => void
  percentFormatId?: string
  valueFormatId?: string
}) => {
  return (
    <StyledSwitchFormat>
      {selectedFormat === OrderDiscountType.PERCENT ||
      selectedFormat === OrderPriceModifierType.PERCENT ? (
        <TextButton id={valueFormatId} onClick={() => onFormatChange('VALUE')}>
          value
        </TextButton>
      ) : (
        <span>value</span>
      )}
      <span>{' / '}</span>
      {selectedFormat === OrderDiscountType.PERCENT ||
      selectedFormat === OrderPriceModifierType.PERCENT ? (
        <span id={sendPaymentRequestIds.numericInputPercentage}>percent</span>
      ) : (
        <TextButton
          id={percentFormatId}
          onClick={() => onFormatChange('PERCENT')}
        >
          percent
        </TextButton>
      )}
    </StyledSwitchFormat>
  )
}

/*
  - selectedFormat and setSelectedFormat are tied to a deprecated backend type OrderDiscountType so should be not used 
  - selectedFormatType and setSelectedFormatType are the new fields that should be used. 
*/
export const NumericFormatInput = ({
  title,
  value,
  handleOnBlur,
  selectedFormat,
  setSelectedFormat,
  selectedFormatType,
  setSelectedFormatType,
  maxAmount,
  isRequired,
  disabled,
  onNotifyChange,
  className,
  id,
  percentFormatId,
  valueFormatId,
}: {
  title: string
  value: number | string
  handleOnBlur: (e: { target: HTMLInputElement }) => void
  selectedFormat?: OrderDiscountType // @deprecated
  setSelectedFormat?: (selectedFormat: OrderDiscountType) => void // @deprecated
  selectedFormatType?: OrderPriceModifierType
  setSelectedFormatType?: (priceMod: OrderPriceModifierType) => void
  maxAmount?: string
  isRequired?: boolean
  disabled?: boolean
  onNotifyChange?: (value: string) => void
  className?: string
  id?: string
  percentFormatId?: string
  valueFormatId?: string
}) => {
  const getMaxAmount = () => {
    if (
      selectedFormat === OrderDiscountType.PERCENT ||
      selectedFormatType === OrderPriceModifierType.PERCENT
    ) {
      return 100
    }
    if (
      maxAmount &&
      (selectedFormat === OrderDiscountType.NUMERIC ||
        selectedFormat === OrderPriceModifierType.NUMERIC)
    ) {
      const numberAmount = Number(maxAmount)
      const numberRounded = Number(numberAmount.toFixed(2))
      return maxAmount && numberRounded
    }
    return undefined
  }

  const handleFormatChange = (selectedType: 'VALUE' | 'PERCENT') => {
    if (selectedType === 'VALUE') {
      setSelectedFormat && setSelectedFormat(OrderDiscountType.NUMERIC)
      setSelectedFormatType &&
        setSelectedFormatType(OrderPriceModifierType.NUMERIC)
      return
    }
    if (selectedType === 'PERCENT') {
      setSelectedFormat && setSelectedFormat(OrderDiscountType.PERCENT)
      setSelectedFormatType &&
        setSelectedFormatType(OrderPriceModifierType.PERCENT)
      return
    }
  }

  return (
    <div className={className}>
      <TitleWrapper className={disabled ? 'disabled' : ''}>
        <StyledInputTitle>
          {title}
          {isRequired && <RequiredAstherisk> *</RequiredAstherisk>}
        </StyledInputTitle>
        {selectedFormat && setSelectedFormat && (
          <FormatSwitch
            selectedFormat={selectedFormat}
            onFormatChange={handleFormatChange}
            percentFormatId={percentFormatId}
          />
        )}
        {selectedFormatType && setSelectedFormatType && (
          <FormatSwitch
            selectedFormat={selectedFormatType}
            onFormatChange={handleFormatChange}
            percentFormatId={percentFormatId}
            valueFormatId={valueFormatId}
          />
        )}
      </TitleWrapper>
      <StyledNumberInput
        id={id}
        percent={
          selectedFormat === OrderDiscountType.PERCENT ||
          selectedFormatType === OrderPriceModifierType.PERCENT
        }
        isRequired={isRequired}
        disabled={disabled}
        onlyNaturalNumbersAllowed={selectedFormat === OrderDiscountType.PERCENT}
        maxAmount={getMaxAmount()}
        defaultValue={value ?? 0}
        onBlur={handleOnBlur}
        scale={2}
        allowComma={false}
        onNotifyChange={onNotifyChange}
      />
    </div>
  )
}
